/* ***************************************************************** */
/*                                                                   */
/* Licensed Materials - Property of IBM                              */
/*                                                                   */
/* (C) Copyright IBM Corp. 2022                                      */
/*                                                                   */
/* ***************************************************************** */

import {getDrugInteractiondetailsPromise} from "../../../services/DrugInteractionService";
import {makeSetChatbotReturnTarget} from "../../ChatbotPage/chatbot-utils";
import {immutable_set, util_alert} from "../../../utils";
import {handleError, handleErrorStatuses} from "../../../utils/internal-error-handler";
import {GENERIC_ERROR_MESSAGE, DRUG_INTERACTION_ERROR_TYPE} from "../../../components/common/constants";
import {
  ACTIVE_STATUS,
  DRUG_INTERACTION_DETAILS_PAGE_NAME, ERROR_STATUS, LOADING_DESCRIPTION, LOADING_ERROR_DESCRIPTION,
  makeResponseLoadingAction, NONE_STATUS
} from "../../../components/response-loading/redux/response-loading-redux";

export const EMPTY_STATE = {
    details_cache: {}
}

export const A = {
    INTERACTION_DETAILS_RESPONSE: 'INTERACTION_DETAILS_RESPONSE',
    CLEAR_INTERACTION_STATE: 'CLEAR_INTERACTION_STATE',
    SET_WARNING_IDS: 'SET_WARNING_IDS',
    SET_DETAILS_CACHE: 'SET_DETAILS_CACHE'
}

export function makeInteractionDetailsResponseAction(interaction_details_response_json={}){
    return {
        type: A.INTERACTION_DETAILS_RESPONSE,
        interaction_details_response_json
    }
}

export function makeInteractionDetailsClearStateAction() {
    return {
        type: A.CLEAR_INTERACTION_STATE
    }
}

export function makeSetWarningIdsAction(warningIds = []) {
    return {
        type: A.SET_WARNING_IDS,
        warningIds
    }
}

/**
 * Build a deterministic cache key from an array of warning IDs.
 * Elements are sorted so order does not matter.
 * Example: warningIds=[345,123] => "123,345"
 * 
 * NOTE: contentSet parameter removed as of NEW backend flow (2026).
 * REASON: Backend now routes ALL requests to same service (DrugInteractionSvc ContentSet-164)
 * regardless of contentSet value, so including it in cache key is unnecessary.
 * Cache key is now simpler: just the sorted warning IDs.
 * 
 * OLD CACHE KEY FORMAT: "DI123,DI345" (included contentSet prefix)
 * NEW CACHE KEY FORMAT: "123,345" (just warning IDs)
 */
export function buildDetailsCacheKey(warningIds) {
    return warningIds
        .map(id => `${id}`)
        .sort()
        .join(',');
}

export function makeSetDetailsCacheAction(cacheKey, response) {
    return {
        type: A.SET_DETAILS_CACHE,
        cacheKey,
        response
    }
}

export function makeDetailsGoToChatbotPageActionThunk(props={}){
    return (dispatch) => {
        dispatch(makeSetChatbotReturnTarget('InteractionDetailsPage'));
        props.history.push('/chatbot');
    }
}

// Reducers ...
/*
WARNING! WARNING! WARNING!

Reducers (and methods invoked from a reducer)
should only return a new state obtained by using immutable_set() on the old state

Reducers should *NEVER* modify any part of the old state!

It is OK to directly modify a portion of the new state that has already been modified by immutable_set() ...
but be *VERY* *VERY* *CAREFUL* to never modify an object that is shared between new and old state!

A *lot* of React functionality depends on reliable detecting the exact differences between the new and old states

WARNING! WARNING! WARNING!
 */

export function interactionDetailsResponseReducer(current_state={}, action={}){
console.debug('inside interaction details reducer')
    return immutable_set(
        current_state,
        'INTERACTION_DETAILS_RESPONSE' ,
        action.interaction_details_response_json
    )
}

export function setWarningIdsInteractionDetailsReducer(current_state={}, action={}){
    return immutable_set(
        current_state,
        'warning_ids' ,
        action.warningIds
    );
}

export function setDetailsCacheReducer(current_state={}, action={}) {
    return immutable_set(
        current_state,
        `details_cache.${action.cacheKey}`,
        action.response
    );
}

export function clearInteractionStateReducer(current_state={}, action={}){
console.debug('inside clear interaction details reducer')
    // Preserve details_cache so previously fetched interactions are not re-fetched
    return  {
        warning_ids:[],
        INTERACTION_DETAILS_RESPONSE:{},
        details_cache: current_state.details_cache || {}
    }
}

//THUNKS
export function makeDrugInteractionGoToChatbotActionThunk(props={}) {
    return (dispatch) => {
        dispatch(makeSetChatbotReturnTarget('DrugInteractions'));
        props.history.push('/chatbot')
    }
}
/**
 * Redux thunk action creator to fetch drug interaction details.
 * 
 * NOTE: contentSet extraction logic removed as of NEW backend flow (2026).
 * REASON: MDX-Services backend no longer returns "contenttype" field in first API response
 * and doesn't use contentSet for routing decisions. All requests now route to the same
 * service (DrugInteractionSvc ContentSet-164).
 * 
 * OLD FLOW:
 *   1. First API returns {warnings: [...], contenttype: "DI"} (conditionally)
 *   2. UI extracts contenttype and stores it
 *   3. Second API includes contentSet: "DI" in request
 *   4. Backend routes based on contentSet value
 * 
 * NEW FLOW:
 *   1. First API returns {warnings: [...]} (no contenttype field)
 *   2. UI doesn't extract/store anything
 *   3. Second API doesn't include contentSet
 *   4. Backend always routes to DI service regardless
 * 
 * CACHE KEY: Now uses only warning IDs (e.g., "123,345" instead of "DI123,DI345")
 */
export function makeDrugDetailsActionThunk( warning_ids=[], props={}){
    return(dispatch, getState) => {
        console.debug(warning_ids);
        console.debug('props in side drug details...', props);
        let current_state=getState();
        let login_page = {};
        
        // NOTE: contenttype extraction removed - backend no longer returns this field
        // All routing is now handled internally by MDX-Services backend
        // No need to extract contenttype from first response or pass contentSet to second call
        
        // Build simple cache key using only warning IDs
        const cacheKey = buildDetailsCacheKey(warning_ids);

        // Check cache for this warning IDs combination
        const detailsCache = (current_state.interaction_details_page || {}).details_cache || {};
        const cachedResponse = detailsCache[cacheKey];
        if (cachedResponse) {
            console.debug('makeDrugDetailsActionThunk: cache hit for key=', cacheKey);
            dispatch(makeInteractionDetailsResponseAction(cachedResponse));
            dispatch(makeSetWarningIdsAction(warning_ids));
            return;
        }

        if(current_state.interaction_details_page) {
            login_page = current_state.login_page;
        }
        dispatch(makeResponseLoadingAction(DRUG_INTERACTION_DETAILS_PAGE_NAME, ACTIVE_STATUS, LOADING_DESCRIPTION));

        // Call API with only warning_ids - no contentSet parameter
        return getDrugInteractiondetailsPromise(
            {
                warning_ids  // contentSet parameter removed - not needed
            }, login_page
        ).then(function (response){
            if(response.status === 200) {
                dispatch(makeResponseLoadingAction(DRUG_INTERACTION_DETAILS_PAGE_NAME, NONE_STATUS, LOADING_DESCRIPTION))
                return response.json();
            } else {
                dispatch(makeResponseLoadingAction(DRUG_INTERACTION_DETAILS_PAGE_NAME, ERROR_STATUS, LOADING_ERROR_DESCRIPTION))
                handleInteractionDetailCommunicationResponse(response, props, dispatch,getState);
            }
        }).then(interaction_details_response_json =>{
            console.debug('drug interaction details ...',interaction_details_response_json );
            // Store response in cache before dispatching
            dispatch(makeSetDetailsCacheAction(cacheKey, interaction_details_response_json));
            dispatch(makeInteractionDetailsResponseAction(interaction_details_response_json));
            dispatch(makeSetWarningIdsAction(warning_ids));
        }).catch(error => {
            dispatch(makeResponseLoadingAction(DRUG_INTERACTION_DETAILS_PAGE_NAME, ERROR_STATUS, LOADING_ERROR_DESCRIPTION))
            handleError({props, message: GENERIC_ERROR_MESSAGE, dispatch}, makeInteractionDetailsResponseAction(
              JSON.stringify("<heading>An error occurred. Please try again. </heading>.")
            ))
            util_alert(
                "An error occurred. Please try again.",
                DRUG_INTERACTION_ERROR_TYPE,
                dispatch,
                getState
            );
        })

    }
}

export function handleInteractionDetailCommunicationResponse(response, props, dispatch,getState) {
  const status = response.status;
  if(status === 200) {
    return response.json();
  } else {
    if (status === 403) {
      dispatch(makeInteractionDetailsClearStateAction())
    }
    let mutable_props = {...props};
    mutable_props.error_type = DRUG_INTERACTION_ERROR_TYPE
    handleErrorStatuses(status, mutable_props, dispatch, getState)
  }
}
export function interaction_details_page(
    current_state = EMPTY_STATE,
    action = {}
) {
    switch(action.type){
        case A.INTERACTION_DETAILS_RESPONSE:
            return interactionDetailsResponseReducer(current_state, action);
        case A.CLEAR_INTERACTION_STATE:
            return clearInteractionStateReducer(current_state, action);
        case A.SET_WARNING_IDS:
            return setWarningIdsInteractionDetailsReducer(current_state, action);
        case A.SET_DETAILS_CACHE:
            return setDetailsCacheReducer(current_state, action);
        default:
            return current_state
    }
}
